import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MENU, ORDER_URL } from "@/data/menu";
import { Reveal, ChapterHeading } from "@/components/Shared";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const MenuSection = () => {
  const [active, setActive] = useState(MENU[0].id);
  const category = MENU.find((c) => c.id === active);

  return (
    <section id="menu" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="menu-section">
      <ChapterHeading
        number="01"
        eyebrow="The Menu"
        title={
          <>
            A feast, <span className="italic text-terracotta">course</span> by course
          </>
        }
        copy="Curries slow-simmered, breads fired to order. Prices in AUD — order online for pickup or delivery."
      />

      <Reveal delay={0.1}>
        <div className="mt-12 flex gap-2 overflow-x-auto pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="menu-tabs" role="tablist">
          {MENU.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={active === c.id}
              data-testid={`menu-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                active === c.id ? "text-[#FFF9F1]" : "text-muted-ink hover:text-forest"
              }`}
            >
              {active === c.id && (
                <motion.span
                  layoutId="menu-tab-pill"
                  className="absolute inset-0 rounded-full bg-forest"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
              <span className="relative z-10">{c.label}</span>
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 grid grid-cols-1 gap-x-14 gap-y-9 md:grid-cols-2"
          data-testid={`menu-panel-${active}`}
          role="tabpanel"
        >
          {category.items.map((item, i) => (
            <motion.article
              key={item.name}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.04, ease: "easeOut" }}
              className="group border-b border-line pb-5"
              data-testid={`menu-item-${slug(item.name)}`}
            >
              <div className="flex items-baseline gap-3">
                <h3 className="font-display text-xl font-semibold text-forest transition-colors duration-300 group-hover:text-terracotta sm:text-2xl">
                  {item.name}
                  {item.note && (
                    <span className="ml-2 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-sage-dark">
                      {item.note}
                    </span>
                  )}
                </h3>
                <span aria-hidden className="mx-1 flex-1 border-b border-dotted border-forest/25" />
                <span className="font-display text-lg font-semibold text-terracotta" data-testid={`menu-price-${slug(item.name)}`}>
                  ${item.price}
                </span>
              </div>
              {item.desc && <p className="mt-1.5 max-w-md text-sm leading-relaxed text-muted-ink">{item.desc}</p>}
              {item.tag && (
                <span className="mt-2 inline-block rounded-full bg-mint px-3 py-1 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-sage-dark">
                  {item.tag}
                </span>
              )}
            </motion.article>
          ))}
        </motion.div>
      </AnimatePresence>

      <Reveal delay={0.05} className="mt-14 flex justify-center">
        <a
          href={ORDER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="menu-order-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-[#FFF9F1] transition-all duration-300 hover:bg-[#B8170F] hover:shadow-[0_14px_30px_-12px_rgba(194,94,56,0.8)]"
        >
          Start an order
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </Reveal>
    </section>
  );
};
